import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import { Button, Input, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServicePageDefacementSites,
  useRemoveServiceAutoBlackList,
  type PageDefacementSite,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './WebAccessControlTab.module.css';

/**
 * AI-WAF → Web Access Control. Two surfaces:
 *   - Website Whitelist: per-website URL paths that bypass AI-WAF detection
 *     (config.website_whitelist); the website list reuses the account /sites read.
 *   - Remove Auto-Web ACL: drop an IP (or all via 0.0.0.0) from the auto Web ACL.
 */
export function WebAccessControlTab() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/waap/${serviceId}`;
  const { show } = useToast();

  const { data: sites = [], isLoading } = useServicePageDefacementSites(serviceId ?? '');

  const [ip, setIp] = useState('');
  const remove = useRemoveServiceAutoBlackList(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'web_acl_blacklist', 'delete');
      show({ tone: 'success', title: 'Removed from the auto Web ACL' });
      setIp('');
    },
    onError: (e) => show({ tone: 'danger', title: 'Remove failed', description: errMessage(e) }),
  });

  const columns = useMemo<ColumnDef<PageDefacementSite>[]>(
    () => [
      {
        accessorKey: 'domain',
        header: 'Website',
        cell: ({ row }) => <span className={styles.name}>{row.original.domain}</span>,
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`${base}/aiwaf/web-access-control/${row.original.site_id}`)}
          >
            Edit
          </Button>
        ),
      },
    ],
    [base, navigate],
  );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs trail={[{ label: 'AI-WAF' }, { label: 'Web Access Control' }]} />
        }
        title="Web Access Control"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Section
        title="Website Whitelist"
        description="Specify the URL paths that will bypass AI-WAF detection."
        style={{ marginBottom: 0 }}
        actions={
          <StatusPill tone={sites.length ? 'info' : 'neutral'}>{sites.length} whitelisted</StatusPill>
        }
      >
        {isLoading ? (
          <div className={styles.skeletonStack}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 48 }} />
            ))}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={sites}
            emptyState={
              <EmptyState
                size="sm"
                icon={<Globe size={20} />}
                title="No whitelisted websites"
                description="No website whitelist entries yet. Add a website to this service, then whitelist URL paths that bypass AI-WAF detection."
              />
            }
          />
        )}
      </Section>

      <Section
        title="Remove Auto-Web ACL"
        description="Enter the IP to remove from the Web ACL. Enter 0.0.0.0 to remove every IP in the ACL."
        style={{ marginBottom: 0 }}
      >
        <div className={styles.inlineRow}>
          <Field label="IP">
            {(props) => (
              <Input
                {...props}
                value={ip}
                placeholder="Remove all: 0.0.0.0"
                className={styles.ipInput}
                onChange={(e) => setIp(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && ip.trim()) remove.mutate(ip.trim());
                }}
              />
            )}
          </Field>
          <Button
            variant="secondary"
            disabled={!ip.trim() || remove.isPending}
            onClick={() => remove.mutate(ip.trim())}
          >
            {remove.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </Section>
      </div>
    </Container>
  );
}
